import React, { useState, useEffect } from 'react';
import { toast } from 'sonner';
import { 
  Code, Save, Loader2, Info, Copy, Check, AlertTriangle
} from 'lucide-react';
import Sidebar from '../components/Sidebar';
import { useAuth } from '../contexts/AuthContext';
import api from '../services/api';

const AdminAnalyticsSettings = () => {
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [headScripts, setHeadScripts] = useState('');
  const [bodyScripts, setBodyScripts] = useState('');
  const [lastUpdated, setLastUpdated] = useState(null);
  const [copiedHead, setCopiedHead] = useState(false);
  const [copiedBody, setCopiedBody] = useState(false);

  const isMainAdmin = user?.role === 'admin' || user?.role === 'main_admin';

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    try {
      const res = await api.get('/admin/settings/analytics-scripts');
      setHeadScripts(res.data.head_scripts || '');
      setBodyScripts(res.data.body_scripts || '');
      setLastUpdated(res.data.updated_at);
    } catch (error) {
      console.error('Failed to fetch analytics settings:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await api.put('/admin/settings/analytics-scripts', {
        head_scripts: headScripts,
        body_scripts: bodyScripts
      });
      toast.success('Analytics scripts saved! Changes will take effect on next page load.');
      setLastUpdated(new Date().toISOString());
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  const copyToClipboard = async (text, type) => {
    try {
      await navigator.clipboard.writeText(text);
      if (type === 'head') {
        setCopiedHead(true);
        setTimeout(() => setCopiedHead(false), 2000);
      } else {
        setCopiedBody(true);
        setTimeout(() => setCopiedBody(false), 2000);
      }
      toast.success('Copied to clipboard!');
    } catch (err) {
      toast.error('Failed to copy');
    }
  };

  // Default GTM code for reference
  const defaultHeadScript = `<!-- Google Tag Manager -->
<script>(function(w,d,s,l,i){w[l]=w[l]||[];w[l].push({'gtm.start':
new Date().getTime(),event:'gtm.js'});var f=d.getElementsByTagName(s)[0],
j=d.createElement(s),dl=l!='dataLayer'?'&l='+l:'';j.async=true;j.src=
'https://www.googletagmanager.com/gtm.js?id='+i+dl;f.parentNode.insertBefore(j,f);
})(window,document,'script','dataLayer','GTM-XXXXXXX');</script>
<!-- End Google Tag Manager -->`;

  const defaultBodyScript = `<!-- Google Tag Manager (noscript) -->
<noscript><iframe src="https://www.googletagmanager.com/ns.html?id=GTM-XXXXXXX"
height="0" width="0" style="display:none;visibility:hidden"></iframe></noscript>
<!-- End Google Tag Manager (noscript) -->`;

  if (!isMainAdmin) {
    return (
      <Sidebar>
        <div className="p-8 text-center">
          <p className="text-red-600">Access denied. Main Admin only.</p>
        </div>
      </Sidebar>
    );
  }

  if (loading) {
    return (
      <Sidebar>
        <div className="p-8 flex items-center justify-center min-h-screen">
          <Loader2 className="w-8 h-8 animate-spin text-orange-600" />
        </div>
      </Sidebar>
    );
  }

  return (
    <Sidebar>
      <div className="p-8 bg-gray-50/50 min-h-screen">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Analytics & Custom Scripts</h1>
            <p className="text-gray-500 mt-1">Add tracking codes, analytics scripts, or custom code to all pages</p>
          </div>
          
          <button
            onClick={handleSave}
            disabled={saving}
            className="flex items-center gap-2 px-5 py-2.5 bg-orange-600 text-white rounded-xl hover:bg-orange-700 disabled:opacity-50"
            data-testid="save-analytics-btn"
          >
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
            Save Changes
          </button>
        </div>

        {/* Info Banner */}
        <div className="mb-6 p-4 bg-blue-50 border border-blue-200 rounded-xl">
          <div className="flex gap-3">
            <Info className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
            <div>
              <h3 className="font-medium text-blue-800 mb-1">Where scripts are injected:</h3>
              <ul className="text-sm text-blue-700 space-y-1">
                <li>• <strong>Head Scripts</strong> — Injected inside <code className="bg-blue-100 px-1 rounded">&lt;head&gt;</code> tag on ALL pages (login, register, dashboard, admin, etc.)</li>
                <li>• <strong>Body Scripts</strong> — Injected right after <code className="bg-blue-100 px-1 rounded">&lt;body&gt;</code> tag on ALL pages</li>
                <li>• Changes take effect on next page load/refresh</li>
              </ul>
            </div>
          </div>
        </div>

        {/* Warning Banner */}
        <div className="mb-6 p-4 bg-amber-50 border border-amber-200 rounded-xl">
          <div className="flex gap-3">
            <AlertTriangle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
            <div>
              <h3 className="font-medium text-amber-800 mb-1">Important:</h3>
              <p className="text-sm text-amber-700">
                Invalid scripts can break your site. Always test in a development environment first. 
                If something goes wrong, clear the scripts and save to restore functionality.
              </p>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 gap-6">
          {/* Head Scripts */}
          <div className="bg-white rounded-2xl border border-gray-200 p-6">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-2">
                <Code className="w-5 h-5 text-purple-600" />
                <h2 className="text-lg font-semibold text-gray-900">Head Scripts</h2>
              </div>
              {headScripts && (
                <button
                  onClick={() => copyToClipboard(headScripts, 'head')}
                  className="flex items-center gap-1 text-sm text-gray-500 hover:text-gray-700"
                >
                  {copiedHead ? <Check className="w-4 h-4 text-green-600" /> : <Copy className="w-4 h-4" />}
                  {copiedHead ? 'Copied!' : 'Copy'}
                </button>
              )}
            </div>
            <p className="text-sm text-gray-500 mb-3">
              Add scripts that should load in the <code className="bg-gray-100 px-1 rounded">&lt;head&gt;</code> section (e.g., Google Tag Manager, Meta Pixel, analytics)
            </p>
            <textarea
              value={headScripts}
              onChange={(e) => setHeadScripts(e.target.value)}
              placeholder={defaultHeadScript}
              className="w-full h-48 px-4 py-3 border border-gray-200 rounded-xl font-mono text-sm focus:outline-none focus:ring-2 focus:ring-purple-500 resize-y"
              data-testid="head-scripts-textarea"
            />
          </div>

          {/* Body Scripts */}
          <div className="bg-white rounded-2xl border border-gray-200 p-6">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-2">
                <Code className="w-5 h-5 text-green-600" />
                <h2 className="text-lg font-semibold text-gray-900">Body Scripts</h2>
              </div>
              {bodyScripts && (
                <button
                  onClick={() => copyToClipboard(bodyScripts, 'body')}
                  className="flex items-center gap-1 text-sm text-gray-500 hover:text-gray-700"
                >
                  {copiedBody ? <Check className="w-4 h-4 text-green-600" /> : <Copy className="w-4 h-4" />}
                  {copiedBody ? 'Copied!' : 'Copy'}
                </button>
              )}
            </div>
            <p className="text-sm text-gray-500 mb-3">
              Add scripts that should load right after <code className="bg-gray-100 px-1 rounded">&lt;body&gt;</code> (e.g., GTM noscript fallback, chat widgets)
            </p>
            <textarea
              value={bodyScripts}
              onChange={(e) => setBodyScripts(e.target.value)}
              placeholder={defaultBodyScript}
              className="w-full h-48 px-4 py-3 border border-gray-200 rounded-xl font-mono text-sm focus:outline-none focus:ring-2 focus:ring-green-500 resize-y"
              data-testid="body-scripts-textarea"
            />
          </div>
        </div>

        {/* Last Updated */}
        {lastUpdated && (
          <p className="mt-4 text-sm text-gray-500 text-right">
            Last updated: {new Date(lastUpdated).toLocaleString()}
          </p>
        )}

        {/* Common Scripts Reference */}
        <div className="mt-8 bg-white rounded-2xl border border-gray-200 p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Common Scripts Reference</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="p-4 bg-gray-50 rounded-xl">
              <h4 className="font-medium text-gray-800 mb-2">Google Tag Manager (Head)</h4>
              <pre className="text-xs text-gray-600 overflow-x-auto whitespace-pre-wrap">
{`<!-- Google Tag Manager -->
<script>(function(w,d,s,l,i){w[l]=w[l]||[];w[l].push({'gtm.start':
new Date().getTime(),event:'gtm.js'});var f=d.getElementsByTagName(s)[0],
j=d.createElement(s),dl=l!='dataLayer'?'&l='+l:'';j.async=true;j.src=
'https://www.googletagmanager.com/gtm.js?id='+i+dl;f.parentNode.insertBefore(j,f);
})(window,document,'script','dataLayer','GTM-XXXXXXX');</script>
<!-- End Google Tag Manager -->`}
              </pre>
            </div>
            <div className="p-4 bg-gray-50 rounded-xl">
              <h4 className="font-medium text-gray-800 mb-2">Google Tag Manager (Body)</h4>
              <pre className="text-xs text-gray-600 overflow-x-auto whitespace-pre-wrap">
{`<!-- Google Tag Manager (noscript) -->
<noscript><iframe src="https://www.googletagmanager.com/ns.html?id=GTM-XXXXXXX"
height="0" width="0" style="display:none;visibility:hidden"></iframe></noscript>
<!-- End Google Tag Manager (noscript) -->`}
              </pre>
            </div>
            <div className="p-4 bg-gray-50 rounded-xl">
              <h4 className="font-medium text-gray-800 mb-2">Google Analytics 4</h4>
              <pre className="text-xs text-gray-600 overflow-x-auto whitespace-pre-wrap">
{`<!-- Google Analytics -->
<script async src="https://www.googletagmanager.com/gtag/js?id=G-XXXXXXX"></script>
<script>
  window.dataLayer = window.dataLayer || [];
  function gtag(){dataLayer.push(arguments);}
  gtag('js', new Date());
  gtag('config', 'G-XXXXXXX');
</script>`}
              </pre>
            </div>
            <div className="p-4 bg-gray-50 rounded-xl">
              <h4 className="font-medium text-gray-800 mb-2">Meta Pixel</h4>
              <pre className="text-xs text-gray-600 overflow-x-auto whitespace-pre-wrap">
{`<!-- Meta Pixel Code -->
<script>
!function(f,b,e,v,n,t,s)
{if(f.fbq)return;n=f.fbq=function(){n.callMethod?
n.callMethod.apply(n,arguments):n.queue.push(arguments)};
if(!f._fbq)f._fbq=n;n.push=n;n.loaded=!0;n.version='2.0';
n.queue=[];t=b.createElement(e);t.async=!0;
t.src=v;s=b.getElementsByTagName(e)[0];
s.parentNode.insertBefore(t,s)}(window,document,'script',
'https://connect.facebook.net/en_US/fbevents.js');
fbq('init', 'YOUR_PIXEL_ID');
fbq('track', 'PageView');
</script>`}
              </pre>
            </div>
          </div>
        </div>
      </div>
    </Sidebar>
  );
};

export default AdminAnalyticsSettings;
